/**
 * Client for the fabled backend (docs/architecture.md "Backend HTTP API").
 * Session-cookie auth + CSRF token on mutating requests.
 */
import type { Edit } from '../format/types';

export interface User {
  id: number;
  username: string;
  /** Gates the AI assistant UI; the server remains the enforcement point. */
  is_staff: boolean;
}

export interface ProjectSummary {
  id: number;
  title: string;
  description: string;
  duration: number;
  created: string;
  modified: string;
}

/** Working-bin entry: a clip of an archive item saved to the project bin. */
export interface BinEntry {
  id: string;
  archive: string;
  item: string;
  title: string;
  duration: number; // concatenated item duration (s)
  parts: number;
  posterFrame?: number;
  inPoint?: number; // concatenated item time (s)
  outPoint?: number;
  note?: string;
  addedAt: string; // ISO timestamp
}

export interface Project {
  id: number;
  title: string;
  description: string;
  timeline: Edit;
  bin: BinEntry[];
  created: string;
  modified: string;
  is_public: boolean;
  public_token: string;
  collab_enabled: boolean;
  collab_token: string;
}

export interface PublishState {
  is_public: boolean;
  public_token?: string;
}

export interface CollabState {
  collab_enabled: boolean;
  collab_token?: string;
}

/**
 * Join payload for a collab-link visitor. Deliberately has no `timeline`: a
 * collaborator's document arrives over the sync socket, never over HTTP.
 */
export interface CollabProject {
  id: number;
  title: string;
  description: string;
  bin: BinEntry[];
}

/** Public viewer payload — no owner/id/private fields. */
export interface PublicProject {
  title: string;
  description: string;
  timeline: Edit;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(`API error ${status}`);
    this.status = status;
    this.body = body;
  }
}

/**
 * Read a cookie value by name. Django sets the `csrftoken` cookie (non-HttpOnly)
 * and rotates it on session cycle (login/logout); reading it fresh on every
 * mutating request means rotation is self-healing — no client-side cache or
 * invalidation needed. See Django docs "How to use Django's CSRF protection".
 */
function getCookie(name: string): string | null {
  const prefix = name + '=';
  for (const part of document.cookie.split(';')) {
    const c = part.trim();
    if (c.startsWith(prefix)) {
      return decodeURIComponent(c.slice(prefix.length));
    }
  }
  return null;
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown } = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const mutating = method !== 'GET' && method !== 'HEAD';
  const headers: Record<string, string> = {};
  if (mutating) {
    // Read the CSRF token from the cookie on every call. Django primes the
    // cookie on the startup GET /api/auth/me and rotates it on login/logout,
    // so the next request always sends the current value.
    const token = getCookie('csrftoken');
    if (token) headers['X-CSRFToken'] = token;
  }
  let body: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }
  const res = await fetch(path, { method, headers, body, credentials: 'include' });
  if (res.status === 204) return undefined as T;
  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const backend = {
  auth: {
    // Fallback primer for the csrftoken cookie. The SPA normally primes it via
    // the startup GET /api/auth/me (which calls get_token); this endpoint stays
    // as a harmless explicit fallback. No token is cached — request() reads the
    // cookie directly on each mutating call.
    csrf: () => request<{ csrfToken: string }>('/api/auth/csrf'),
    register: (username: string, password: string) =>
      request<{ user: User }>('/api/auth/register', {
        method: 'POST',
        body: { username, password },
      }),
    login: (username: string, password: string) =>
      request<{ user: User }>('/api/auth/login', {
        method: 'POST',
        body: { username, password },
      }),
    logout: () =>
      request<Record<string, never>>('/api/auth/logout', { method: 'POST' }),
    me: () => request<{ user: User | null }>('/api/auth/me'),
  },
  projects: {
    list: () => request<ProjectSummary[]>('/api/projects'),
    create: (title: string, description?: string) =>
      request<Project>('/api/projects', { method: 'POST', body: { title, description } }),
    get: (id: number | string) => request<Project>(`/api/projects/${id}`),
    patch: (
      id: number | string,
      patch: Partial<Pick<Project, 'title' | 'description' | 'timeline' | 'bin'>>,
    ) => request<Project>(`/api/projects/${id}`, { method: 'PATCH', body: patch }),
    delete: (id: number | string) =>
      request<void>(`/api/projects/${id}`, { method: 'DELETE' }),
    duplicate: (id: number | string) =>
      request<Project>(`/api/projects/${id}/duplicate`, { method: 'POST' }),
    publish: (id: number | string) =>
      request<PublishState>(`/api/projects/${id}/publish`, { method: 'POST' }),
    unpublish: (id: number | string) =>
      request<PublishState>(`/api/projects/${id}/publish`, { method: 'DELETE' }),
    enableCollab: (id: number | string) =>
      request<CollabState>(`/api/projects/${id}/collab`, { method: 'POST' }),
    disableCollab: (id: number | string) =>
      request<CollabState>(`/api/projects/${id}/collab`, { method: 'DELETE' }),
  },
  public: {
    get: (token: string) =>
      request<PublicProject>(`/api/public/projects/${encodeURIComponent(token)}`),
  },
  collab: {
    join: (token: string) =>
      request<CollabProject>(`/api/collab/${encodeURIComponent(token)}`),
  },
};
